import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  SUPPORTED_LANGUAGES,
  SupportedLanguage,
} from "./types";

import en from "./locales/en.json";
import es from "./locales/es.json";
import fr from "./locales/fr.json";
import ar from "./locales/ar.json";

export const resources = {
  en: { translation: en },
  es: { translation: es },
  fr: { translation: fr },
  ar: { translation: ar },
} as const;

export function getInitialLanguage(): SupportedLanguage {
  if (typeof window === "undefined") return DEFAULT_LANGUAGE;

  try {
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (saved && SUPPORTED_LANGUAGES.some((l) => l.code === saved)) {
      return saved as SupportedLanguage;
    }

    const browserLang = navigator.language?.split("-")[0]?.toLowerCase();
    if (
      browserLang &&
      SUPPORTED_LANGUAGES.some((l) => l.code === browserLang)
    ) {
      return browserLang as SupportedLanguage;
    }
  } catch (e) {
    console.warn("Unable to access localStorage for language:", e);
  }

  return DEFAULT_LANGUAGE;
}

export function applyDocumentDirection(lang: SupportedLanguage): void {
  if (typeof document === "undefined") return;

  const isRtl = lang === "ar";
  document.documentElement.dir = isRtl ? "rtl" : "ltr";
  document.documentElement.lang = lang;
  if (isRtl) {
    document.documentElement.classList.add("rtl");
  } else {
    document.documentElement.classList.remove("rtl");
  }
}

const initialLang = getInitialLanguage();

i18n.use(initReactI18next).init({
  resources,
  lng: initialLang,
  fallbackLng: DEFAULT_LANGUAGE,
  interpolation: {
    escapeValue: false, // React already safeguards against XSS
  },
  parseMissingKeyHandler: (key: string, defaultValue?: string) => {
    if (defaultValue) return defaultValue;

    // Check if the English locale contains this key
    const parts = key.split(".");
    let cur: unknown = en;
    for (const part of parts) {
      if (cur && typeof cur === "object" && part in cur) {
        cur = (cur as Record<string, unknown>)[part];
      } else {
        cur = undefined;
        break;
      }
    }
    if (typeof cur === "string" && cur) {
      return cur;
    }

    // Format camelCase last segment as readable fallback (e.g. "editButton" -> "Edit Button")
    const lastPart = parts[parts.length - 1] || key;
    const humanized = lastPart
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (s) => s.toUpperCase())
      .trim();

    return humanized || key;
  },
});

applyDocumentDirection(initialLang);

i18n.on("languageChanged", (lng) => {
  const supported = SUPPORTED_LANGUAGES.some((l) => l.code === lng)
    ? (lng as SupportedLanguage)
    : DEFAULT_LANGUAGE;
  applyDocumentDirection(supported);
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, supported);
  } catch (e) {
    console.warn("Failed to persist language preference:", e);
  }
});

export default i18n;
